import { BlobStorageModel } from '../gql-types.generated';
import { FileError } from 'react-dropzone';

/**
 * Sanitizes a file name to only allow alphanumeric, underscore, hyphen, and period characters.
 * @param input - The file name to sanitize
 * @param isDisabled - When true, returns input as-is (e.g. when field is read-only)
 */
export const sanitizeFileName = (input: string, isDisabled: boolean = false): string => {
    if (isDisabled) return input;
    return input.replace(/[^a-zA-Z0-9._-]/g, '');
};

/**
 * Validates that a file name has one of the allowed extensions (case-insensitive).
 * @param fileName - The file name to validate
 * @param allowedExtensions - Array of allowed extensions including the dot (e.g. ['.xsl', '.xslt'])
 * @returns An error message string, or empty string if valid
 */
export const validateFileExtension = (fileName: string, allowedExtensions: string[]): string => {
    if (!fileName.trim()) return '';
    const extension = '.' + fileName.split('.').pop()?.toLowerCase();
    const normalized = allowedExtensions.map(ext => ext.toLowerCase());
    if (!extension || !normalized.includes(extension)) {
        return `File extension must be ${allowedExtensions.join(' or ')}`;
    }
    return '';
};

/**
 * react-dropzone customValidator that rejects files whose extension is not in the allowed list.
 * @param file - The file being validated
 * @param allowedExtensions - Array of allowed extensions including the dot (e.g. ['.xsl', '.xslt'])
 * @returns Array of FileError if invalid, null if valid
 */
export const dropZoneExtensionValidator = (file: File, allowedExtensions: string[]): FileError[] | null => {
    const validationErrorMessage = validateFileExtension(file.name, allowedExtensions);
    if (validationErrorMessage?.length > 0) {
        return [{ code: 'invalid-extension', message: validationErrorMessage }] as FileError[];
    }
    return null;
};

/**
 * Gets the first version (lowest version number) from a list of blob storage versions
 * @param fileVersions - Array of blob storage file versions
 * @returns The blob storage version with the lowest version number, or undefined if no versions exist
 */
export const getFirstBlobStorageVersion = (
    fileVersions: BlobStorageModel[] | undefined
): BlobStorageModel | undefined => {
    if (!fileVersions || fileVersions.length === 0) {
        return undefined;
    }
    
    return fileVersions.reduce((prev, current) => {
        return (current.version < prev.version) ? current : prev;
    });
};